import {
    ErrorWithContext,
    FormContainer,
    FormattedAlert,
    FormattedDialogTitle,
    FormattedFormRow,
    MultipleLineError,
    useErrorMessages,
} from "@dvrt/graphql-widgets";
import {Button, Dialog, DialogActions, DialogContent, TextField} from "@mui/material";
import {useStyles} from "./call-group/members/styles";
import {ReactNode} from "react";
import {defineMessage, FormattedMessage, MessageDescriptor} from "react-intl";
import LoadingScreen from "./LoadingScreen";
import {errorToErrorWithContext} from "@/tools";

/**
 * All the needed information about quota for a domain to be displayed in the UI.
 * */
export interface QuotaInfo {
    domainAllocated: number;
    domainUsed: number;
    remaining: number;
    allocationIncrementError?: ErrorWithContext;
}

/**
 * This interface represents all the needed information about a domain allocation.
 * Not using types, as different queries might return more or less fields.
 * This structure however can not be used directly in components, as the error field
 * is not ErrorWithContext type, so we need to convert this object to [QuotaInfo] first
 * using [domainAllocationToQuotaInfo] function.
 * */
export interface DomainAllocationInfo {
    domainAllocated: number;
    domainUsed: number;
    remaining: number;
    allocationIncrementError?: {
        type: string;
        context?: string;
    };
}

/**
 * Converts a [DomainAllocationInfo] object to a [QuotaInfo] object to be used in components.
 * */
export const domainAllocationToQuotaInfo = (allocation: DomainAllocationInfo): QuotaInfo => ({
    ...allocation,
    allocationIncrementError: errorToErrorWithContext(allocation?.allocationIncrementError),
});

export type ContentQuotaInfoProps = {
    quotaInfo?: QuotaInfo;
    errors?: ErrorWithContext[];
};

export const EndpointLimitInfo = ({quotaInfo, errors}: ContentQuotaInfoProps) => {
    if (quotaInfo)
        return (
            <FormattedFormRow label={defineMessage({id: "quota_status", defaultMessage: "Total / used quota"})}>
                <TextField
                    variant="standard"
                    margin="dense"
                    disabled
                    value={`${quotaInfo.domainAllocated} / ${quotaInfo.domainUsed}`}
                    error={!!errors}
                    helperText={<MultipleLineError errors={errors} />}
                />
            </FormattedFormRow>
        );
    else return null;
};

export type EndpointCreateDialogProps = {
    title: MessageDescriptor;
    isOpen: boolean;
    closeDialog: () => void;
    quotaInfo?: QuotaInfo;
    loading?: boolean;
    children: ReactNode;
};

const LoadingWrapper = ({loading, children}: {loading?: boolean; children: ReactNode}) => {
    return loading ? <LoadingScreen /> : children;
};

const EndpointCreateDialog = ({
    title,
    isOpen,
    closeDialog,
    quotaInfo,
    loading,
    children,
}: EndpointCreateDialogProps) => {
    const {classes} = useStyles();

    const errorMessages = useErrorMessages();
    const canAdd = !quotaInfo?.allocationIncrementError;

    return (
        <Dialog open={isOpen} maxWidth="md">
            <FormattedDialogTitle title={title} />
            <LoadingWrapper loading={loading}>
                {canAdd ? (
                    children
                ) : (
                    <>
                        <DialogContent>
                            <FormContainer>
                                <EndpointLimitInfo quotaInfo={quotaInfo} />
                            </FormContainer>
                            <div className={classes.noMembersContainer}>
                                <FormattedAlert
                                    message={errorMessages[quotaInfo?.allocationIncrementError?.type]}
                                    values={quotaInfo?.allocationIncrementError?.context}
                                    severity="error"
                                />
                            </div>
                        </DialogContent>
                        <DialogActions>
                            <Button variant="text" color="secondary" onClick={closeDialog} sx={{width: "fit-content"}}>
                                <FormattedMessage id="close" defaultMessage="Close" />
                            </Button>
                        </DialogActions>
                    </>
                )}
            </LoadingWrapper>
        </Dialog>
    );
};

export default EndpointCreateDialog;
